import { Tabs } from '@chakra-ui/react';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { TabContentCategory, TabData } from 'types/tab';
import { TabBar, TabBarVariant } from 'components/Article/TabBar';
import { useSearchParams } from 'react-router-dom';
import { NoContentTextWrapper } from 'components/common/NoContentTextWrapper';
import { PageContext } from 'components/common/PageContext';
import { FileIcon, ImageIcon, ListIcon, ScienceIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { SearchResultList } from 'components/Search/SearchResultList';
import { SearchResultListAll } from 'components/Search/SearchResultListAll';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { useSearchWithTypeCounts } from 'hooks/api/backend/search/useSearchWithTypeCounts';
import { useBreadcrumbs } from '../../hooks/utils/useBreadcrumbs';

export const SearchResults = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const tabParam = searchParams.get('t');
  const { searchTypeCounts, refine } = useSearchWithTypeCounts();
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const { hideBreadcrumbs, showBreadcrumbs } = useBreadcrumbs();

  // Sync InstantSearch from the URL when navigation changes `q`.
  // Do not depend on InstantSearch's live query — typing in the search bar
  // updates that value before submit, and reacting to it would snap suggestions
  // back to the previous URL search.
  useEffect(() => {
    refine(query);
  }, [query, refine]);

  // Initialize active tab from URL parameter
  useEffect(() => {
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!Number.isNaN(tabIndex) && tabIndex >= 0) {
        setActiveTabIndex(tabIndex);
      }
    }
  }, [tabParam]);

  // Handle tab change and update URL
  const handleTabChange = (tabIndex: number): void => {
    setActiveTabIndex(tabIndex);
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabIndex === 0) {
      newSearchParams.delete('t');
    } else {
      newSearchParams.set('t', tabIndex.toString());
    }
    setSearchParams(newSearchParams);
  };

  const tabs: TabData[] = useMemo(
    () => [
      {
        id: 0,
        tabName: 'Alle',
        // TODO: replace with ListIcon chakra-ui-v3 upgrade
        icon: <NovaIcon icon={ListIcon} size="s" aria-hidden />,
        categories: [TabContentCategory.ALL],
        num:
          (searchTypeCounts?.lernkarte ?? 0) +
          (searchTypeCounts?.medikamente ?? 0) +
          (searchTypeCounts?.medien ?? 0),
        searchContentTypes: [],
        numOfColumns: 1,
      },
      {
        id: 1,
        tabName: 'Lernkarten',
        icon: <NovaIcon icon={FileIcon} size="s" aria-hidden />,
        num: searchTypeCounts?.lernkarte ?? 0,
        categories: [TabContentCategory.ARTICLE],
        numOfColumns: 1,
      },
      {
        id: 2,
        tabName: 'Medien',
        icon: <NovaIcon icon={ImageIcon} size="s" aria-hidden />,
        num: searchTypeCounts?.medien ?? 0,
        categories: [TabContentCategory.MEDIA],
        numOfColumns: isDesktop ? 3 : 2,
      },
      {
        id: 3,
        tabName: 'Medikamente',
        icon: <NovaIcon icon={ScienceIcon} size="s" aria-hidden />,
        num: searchTypeCounts?.medikamente ?? 0,
        categories: [TabContentCategory.MEDICATION],
        numOfColumns: 1,
      },
    ],
    [isDesktop, searchTypeCounts]
  );

  useEffect(() => {
    hideBreadcrumbs();
    return () => {
      showBreadcrumbs();
    };
  }, [hideBreadcrumbs, showBreadcrumbs]);

  return (
    <PageContext title="Suche" hasSearch headTitle="Suchergebnisse">
      <TabBar
        tabs={tabs}
        variant={TabBarVariant.SEARCH}
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
      >
        {tabs.map((tab, index) => (
          <Tabs.Content value={tab.tabName} padding={0} key={tab.id}>
            {tab.num && tab.num > 0 ? (
              <>
                {index === 0 && (
                  <SearchResultListAll
                    query={query}
                    numOfColumns={tab.numOfColumns}
                    handleTabChange={handleTabChange}
                  />
                )}
                {index !== 0 && (
                  <SearchResultList
                    query={query}
                    title={tab.tabName}
                    numOfColumns={tab.numOfColumns}
                    key={tab.id}
                    icon={tab.icon}
                    categories={tab.categories ?? []}
                    isActive={index === activeTabIndex}
                  />
                )}
              </>
            ) : (
              <NoContentTextWrapper>
                Leider wurden keine {tab.tabName} für &quot;{query}&quot; gefunden.
              </NoContentTextWrapper>
            )}
          </Tabs.Content>
        ))}
      </TabBar>
    </PageContext>
  );
};
